// src/pages/EventManagers.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, orderBy, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import {
  FiUsers,
  FiSearch,
  FiFilter,
  FiFileText,
  FiFolder,
  FiEye,
  FiCheck,
} from "react-icons/fi";

/* Theme */
const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  success: "#16a34a",
  warn: "#b91c1c",
  page: "#f5f6f8",
};

function safeGetEmployee() {
  try {
    const raw = localStorage.getItem("employee");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export default function EventManagers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // permission gate
  const [accessDenied, setAccessDenied] = useState(false);

  // viewer modal
  const [viewer, setViewer] = useState({
    open: false,
    title: "",
    mode: "single", // 'single' | 'docs'
    url: "",
    docs: { panUrl: "", chequeUrl: "" },
  });

  // toast
  const [toast, setToast] = useState("");

  // client-side filters
  const [q, setQ] = useState("");
  const [statusView, setStatusView] = useState("All"); // All | pending | approved

  useEffect(() => {
    const emp = safeGetEmployee();
    const isSuperAdmin = emp?.role === "super_admin";
    const perms = Array.isArray(emp?.permissions) ? emp.permissions : [];
    const hasPermission =
      isSuperAdmin || perms.includes("view_event_managers") || perms.includes("view_organiser_signups");

    if (!hasPermission) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }

    const qy = query(collection(db, "organiser_signup"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs.map((d) => {
          const x = d.data() || {};
          const org = x.general?.organisation || {};
          const contact = x.general?.contact || {};
          const docs = x.documents || {};
          const agr = x.agreement || {};
          return {
            id: d.id,
            orgName: org.name || "—",
            fullName: contact.name || "—",
            createdAt: x.createdAt || null,
            status: String(x.status || "pending").toLowerCase(),
            signedUrl: agr.signedUrl || "",
            panUrl: docs.panUrl || "",
            chequeUrl: docs.cancelledChequeUrl || "",
          };
        });
        setRows(list);
        setLoading(false);
      },
      (e) => {
        console.error("EventManagers onSnapshot error:", e);
        setAccessDenied(true);
        setErr("You do not have permission to read organiser signups.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    let arr = [...rows];

    // search
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      arr = arr.filter((r) => `${r.orgName} ${r.fullName} ${r.status}`.toLowerCase().includes(s));
    }

    // status filter (view mode)
    if (statusView !== "All") {
      arr = arr.filter((r) => r.status === statusView);
    }

    // sort pending first, then by createdAt desc
    return arr.sort((a, b) => {
      const rank = { pending: 0, approved: 1 };
      const ar = rank[a.status] ?? 99;
      const br = rank[b.status] ?? 99;
      if (ar !== br) return ar - br;
      const am = toMillis(a.createdAt);
      const bm = toMillis(b.createdAt);
      return bm - am;
    });
  }, [rows, q, statusView]);

  const pending = useMemo(() => filtered.filter((r) => r.status === "pending"), [filtered]);
  const approved = useMemo(() => filtered.filter((r) => r.status === "approved"), [filtered]);

  const [changing, setChanging] = useState({}); // { [id]: boolean }
  async function applyStatus(id, newStatus) {
    try {
      setChanging((m) => ({ ...m, [id]: true }));
      await updateDoc(doc(db, "organiser_signup", id), {
        status: String(newStatus || "pending").toLowerCase(),
      });
      showToast("Status updated successfully.");
    } catch (e) {
      console.error(e);
      showToast("Update failed. Try again.");
    } finally {
      setChanging((m) => ({ ...m, [id]: false }));
    }
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(""), 2200);
  }

  function openAgreement(url) {
    if (!url) return;
    setViewer({ open: true, title: "Agreement", mode: "single", url, docs: { panUrl: "", chequeUrl: "" } });
  }
  function openDocuments(panUrl, chequeUrl) {
    if (!panUrl && !chequeUrl) return;
    setViewer({ open: true, title: "Documents", mode: "docs", url: "", docs: { panUrl, chequeUrl } });
  }
  function closeViewer() {
    setViewer((v) => ({ ...v, open: false }));
  }

  if (accessDenied) {
    return (
      <div style={styles.deniedWrap}>
        <div style={styles.deniedCard}>
          <FiUsers size={24} color="#e53935" />
          <h3 style={{ marginTop: 8 }}>Access Denied</h3>
          <p>You do not have permission to view this page.</p>
          {err && <p style={{ color: "#b45309", marginTop: 8 }}>{err}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="aa-pro-page" style={styles.container}>
      {/* Hero */}
      <div className="aa-pro-hero" style={styles.hero}>
        <div style={styles.heroLeft}>
          <div className="aa-pro-hero-icon">
            <FiUsers />
          </div>
          <div>
            <h2 className="aa-pro-title">Event Managers</h2>
            <p style={styles.heroSub}>Organiser signups — live, searchable, and reviewable</p>
          </div>
        </div>
        <div className="aa-pro-metrics" style={styles.counters}>
          <Badge tone="pending">Pending: {rows.filter((r) => r.status === "pending").length}</Badge>
          <Badge tone="ok">Approved: {rows.filter((r) => r.status === "approved").length}</Badge>
        </div>
      </div>

      {/* Controls */}
      <div className="aa-pro-toolbar" style={styles.controlsRow}>
        <div style={styles.searchBox}>
          <FiSearch style={{ opacity: 0.7 }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by organisation or full name…"
            style={styles.searchInput}
          />
        </div>
        <div style={styles.controlGroup}>
          <FiFilter />
          <select value={statusView} onChange={(e) => setStatusView(e.target.value)} style={styles.selectBare}>
            <option>All</option>
            <option value="pending">pending</option>
            <option value="approved">approved</option>
          </select>
        </div>
      </div>

      {/* Sections */}
      {loading && <div style={{ color: COLORS.subtext }}>Loading…</div>}
      {!loading && (
        <>
          {statusView !== "approved" && (
            <Section title={`Pending (${pending.length})`}>
              <ManagersTable
                rows={pending}
                onViewAgreement={openAgreement}
                onViewDocs={openDocuments}
                onApply={applyStatus}
                changing={changing}
              />
            </Section>
          )}

          {statusView !== "pending" && (
            <Section title={`Approved (${approved.length})`} topGap>
              <ManagersTable
                rows={approved}
                onViewAgreement={openAgreement}
                onViewDocs={openDocuments}
                onApply={applyStatus}
                changing={changing}
              />
            </Section>
          )}
        </>
      )}

      {/* Modal */}
      {viewer.open && (
        <Modal onClose={closeViewer} title={viewer.title}>
          {viewer.mode === "single" && <MediaViewer url={viewer.url} />}
          {viewer.mode === "docs" && (
            <div style={{ display: "grid", gap: 12 }}>
              {viewer.docs.panUrl ? (
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>PAN</div>
                  <MediaViewer url={viewer.docs.panUrl} />
                </div>
              ) : null}
              {viewer.docs.chequeUrl ? (
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Cancelled Cheque</div>
                  <MediaViewer url={viewer.docs.chequeUrl} />
                </div>
              ) : null}
              {!viewer.docs.panUrl && !viewer.docs.chequeUrl && (
                <div style={{ color: COLORS.subtext }}>No documents uploaded.</div>
              )}
            </div>
          )}
        </Modal>
      )}

      {/* Toast */}
      {toast && (
        <div style={styles.toast}>
          {toast}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */
/* Parts                                                      */
/* ────────────────────────────────────────────────────────── */
function Section({ title, children, topGap }) {
  return (
    <div style={{ marginTop: topGap ? 18 : 0 }}>
      <h3 style={{ margin: "10px 0", color: COLORS.text }}>{title}</h3>
      <div className="aa-pro-table-shell" style={styles.card}>{children}</div>
    </div>
  );
}

function ManagersTable({ rows, onViewAgreement, onViewDocs, onApply, changing }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={styles.table}>
        <thead>
          <tr>
            <Th sticky>Organisation</Th>
            <Th sticky>Full Name</Th>
            <Th sticky>Created</Th>
            <Th sticky>Agreement</Th>
            <Th sticky>Documents</Th>
            <Th sticky>Status</Th>
            <Th sticky>Update</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} style={{ padding: 14, color: COLORS.subtext }}>
                No records.
              </td>
            </tr>
          ) : (
            rows.map((r, i) => {
              const bg = i % 2 ? "#fafafa" : "#fff";
              return (
                <tr
                  key={r.id}
                  style={{
                    borderTop: `1px solid ${COLORS.border}`,
                    background: bg,
                    transition: "background .15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = bg)}
                >
                  <Td>{r.orgName}</Td>
                  <Td>{r.fullName}</Td>
                  <Td mono title={formatDate(r.createdAt)}>{formatDate(r.createdAt)}</Td>
                  <Td>
                    <IconLink disabled={!r.signedUrl} onClick={() => onViewAgreement(r.signedUrl)} title="View agreement">
                      <FiFileText />
                      View
                    </IconLink>
                  </Td>
                  <Td>
                    <IconLink disabled={!r.panUrl && !r.chequeUrl} onClick={() => onViewDocs(r.panUrl, r.chequeUrl)} title="View documents">
                      <FiFolder />
                      View
                    </IconLink>
                  </Td>
                  <Td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <StatusChip status={r.status} />
                      <StatusSelect id={r.id} initial={r.status} />
                    </div>
                  </Td>
                  <Td>
                    <ApplyButton id={r.id} onApply={onApply} changing={!!changing[r.id]} />
                  </Td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

/* Atoms */
function Th({ children, sticky }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: 10,
        fontWeight: 800,
        color: COLORS.text,
        background: "#f8fafc",
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 5,
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      {children}
    </th>
  );
}
function Td({ children, mono, title }) {
  return (
    <td
      title={title}
      style={{
        padding: 10,
        color: COLORS.text,
        verticalAlign: "top",
        fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "inherit",
      }}
    >
      {children}
    </td>
  );
}
function IconLink({ children, onClick, disabled, title }) {
  return (
    <button
      title={title}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        border: `1px solid ${disabled ? "#e5e7eb" : COLORS.border}`,
        background: "#fff",
        color: disabled ? "#9ca3af" : COLORS.text,
        padding: "6px 10px",
        borderRadius: 8,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {children}
    </button>
  );
}
function StatusChip({ status }) {
  const s = String(status || "").toLowerCase();
  const map = {
    pending: { bg: "#fff7ed", fg: "#b45309", label: "pending" },
    approved: { bg: "#ecfdf5", fg: COLORS.success, label: "approved" },
  };
  const v = map[s] || { bg: "#f1f5f9", fg: COLORS.subtext, label: s || "—" };
  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        background: v.bg,
        color: v.fg,
        fontWeight: 800,
        border: `1px solid ${v.fg}33`,
        textTransform: "capitalize",
      }}
    >
      {v.label}
    </span>
  );
}
function StatusSelect({ id, initial }) {
  const [val, setVal] = useState(String(initial || "pending").toLowerCase());
  return (
    <select
      data-id={id}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      style={styles.selectSmall}
      id={`status-${id}`}
    >
      <option value="pending">Pending</option>
      <option value="approved">Approved</option>
    </select>
  );
}
function ApplyButton({ id, onApply, changing }) {
  return (
    <button
      style={styles.applyBtn}
      disabled={changing}
      onClick={() => {
        const el = document.getElementById(`status-${id}`);
        const next = el ? el.value : "pending";
        onApply(id, next);
      }}
      title="Apply status"
    >
      <FiCheck />
      {changing ? "Updating…" : "Update"}
    </button>
  );
}

/* Modal: closes on outside click + always on top */
function Modal({ title, onClose, children }) {
  return (
    <div style={styles.modalBackdrop} onClick={onClose} role="dialog" aria-modal="true">
      <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontWeight: 800, color: COLORS.text }}>{title}</div>
          <button onClick={onClose} style={styles.iconBtn} aria-label="Close">
            ✕
          </button>
        </div>
        <div>{children}</div>
      </div>
    </div>
  );
}

/* Media viewer: PDF / image / fallback link */
function MediaViewer({ url }) {
  const isPdf = /\.pdf(\?|$)/i.test(url);
  const isImg = /\.(png|jpe?g|webp|gif|bmp|svg)(\?|$)/i.test(url);

  if (isPdf) {
    return (
      <div style={styles.viewerFrameBox}>
        <iframe title="PDF" src={url} style={styles.pdfFrame} />
      </div>
    );
  }

  if (isImg) {
    return (
      <div style={styles.imgBox}>
        <img alt="document" src={url} style={styles.imgStyle} />
      </div>
    );
  }

  return (
    <a href={url} target="_blank" rel="noreferrer" style={styles.linkBtn}>
      <FiEye /> Open file
    </a>
  );
}

/* ────────────────────────────────────────────────────────── */
/* Helpers                                                    */
/* ────────────────────────────────────────────────────────── */
function toMillis(ts) {
  try {
    if (!ts) return 0;
    if (typeof ts === "number") return ts;
    if (ts?.toDate) return ts.toDate().getTime();
    const d = new Date(ts);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  } catch {
    return 0;
  }
}
function formatDate(ts) {
  try {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
  } catch {
    return "—";
  }
}

/* ────────────────────────────────────────────────────────── */
/* Styles                                                     */
/* ────────────────────────────────────────────────────────── */
const styles = {
  container: {
    padding: 16,
    fontFamily: "Inter, system-ui, sans-serif",
    backgroundColor: COLORS.page,
    minHeight: "100vh",
    display: "grid",
    gap: 12,
  },

  hero: {
    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
    color: "#fff",
    padding: "14px 16px",
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 14px 26px rgba(0,0,0,0.12)",
  },
  heroLeft: { display: "flex", alignItems: "center", gap: 12 },
  heroIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.35)",
  },
  heroTitle: { margin: 0, fontSize: 20, fontWeight: 900, lineHeight: 1.1 },
  heroSub: { margin: "4px 0 0", opacity: 0.92, fontSize: 13 },
  counters: { display: "flex", gap: 8, flexWrap: "wrap" },

  controlsRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    padding: "10px 12px",
    background: "#fff",
    minWidth: 260,
    flex: 1,
  },
  searchInput: {
    border: "none",
    outline: "none",
    flex: 1,
    fontSize: 14,
  },
  controlGroup: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    padding: "8px 10px",
    background: "#fff",
  },
  selectBare: {
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: 14,
    paddingRight: 4,
    cursor: "pointer",
  },

  card: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 6px 14px rgba(0,0,0,.06)",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 860,
    fontSize: 14,
  },

  selectSmall: {
    height: 34,
    padding: "0 10px",
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    background: "#fff",
  },
  applyBtn: {
    height: 36,
    padding: "0 12px",
    borderRadius: 8,
    border: "none",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.55)",
    display: "grid",
    placeItems: "center",
    zIndex: 9999,
    padding: 16,
  },
  modalCard: {
    width: "min(96vw, 900px)",
    maxHeight: "90vh",
    overflow: "auto",
    background: "#fff",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 14,
    boxShadow: "0 18px 60px rgba(0,0,0,.35)",
  },
  iconBtn: {
    border: `1px solid ${COLORS.border}`,
    background: "#fff",
    color: COLORS.text,
    width: 34,
    height: 34,
    borderRadius: 8,
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
  },

  viewerFrameBox: {
    width: "100%",
    height: "70vh",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    overflow: "hidden",
  },
  pdfFrame: { width: "100%", height: "100%", border: "none" },
  imgBox: {
    display: "grid",
    placeItems: "center",
    maxHeight: "70vh",
    overflow: "auto",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: 8,
  },
  imgStyle: {
    maxWidth: "100%",
    height: "auto",
    maxHeight: "68vh",
    objectFit: "contain",
  },
  linkBtn: {
    border: "none",
    background: "#fff",
    color: COLORS.text,
    padding: "6px 10px",
    borderRadius: 8,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    borderColor: COLORS.border,
    borderStyle: "solid",
    borderWidth: 1,
    cursor: "pointer",
    textDecoration: "none",
  },

  toast: {
    position: "fixed",
    bottom: 16,
    right: 16,
    background: "#fff",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: "10px 12px",
    boxShadow: "0 6px 20px rgba(0,0,0,.12)",
    color: COLORS.text,
    fontWeight: 700,
    zIndex: 9000,
  },

  deniedWrap: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    background: COLORS.page,
    padding: 16,
    fontFamily: "Inter, system-ui, sans-serif",
  },
  deniedCard: {
    background: "#fff",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 16,
    width: "min(92vw, 560px)",
    textAlign: "center",
    boxShadow: "0 10px 30px rgba(0,0,0,.12)",
    color: COLORS.text,
  },
};

/* Small badge */
function Badge({ tone = "ok", children }) {
  const map = {
    ok: { bg: "#ecfdf5", fg: COLORS.success },
    pending: { bg: "#fff7ed", fg: "#b45309" },
  };
  const v = map[tone] || map.ok;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 999,
        background: v.bg,
        color: v.fg,
        fontWeight: 800,
        border: `1px solid ${v.fg}33`,
      }}
    >
      {children}
    </span>
  );
}
